import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import ContactDialog from './components/ContactDialog';
import SEO from './components/SEO';
import LoadingScreen from './components/LoadingScreen';
import {
  SEO_DATA,
  BRAND_NAME,
  MENU_ITEMS,
  HERO_SECTION,
  ABOUT_ME_SECTION,
  ACHIEVEMENTS_SECTION,
  APPROACH_SECTION,
  BENEFITS_SECTION,
  TARGET_AUDIENCE_SECTION,
  OVERCOMING_FEARS_SECTION,
  LEARNING_FORMATS_SECTION,
  CONTACT_SECTION,
  FOOTER,
  TESTIMONIALS,
  AVITO_LINK,
  PROFI_LINK
} from './constants/texts';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [currentSection, setCurrentSection] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const menuRef = useRef(null);
  const heroHeightSet = useRef(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Group testimonials into slides (3 per slide on desktop, 1 on mobile)
  const testimonialSlides = [];
  const itemsPerSlide = isMobile ? 1 : 3;
  for (let i = 0; i < TESTIMONIALS.length; i += itemsPerSlide) {
    testimonialSlides.push(TESTIMONIALS.slice(i, i + itemsPerSlide));
  }

  // Update totalSlides based on the number of testimonial slides
  const totalSlides = testimonialSlides.length;


  const updateCurrentSection = useCallback(() => {
    const sections = ['benefits', 'target-audience', 'overcoming-fears', 'testimonials', 'learning-formats', 'contact'];
    const scrollPosition = window.scrollY + 100;

    for (const section of sections) {
      const element = document.getElementById(section);
      if (element) {
        const top = element.offsetTop - 100;
        const bottom = top + element.offsetHeight;

        if (scrollPosition >= top && scrollPosition < bottom) {
          setCurrentSection(section);
          break;
        }
      }
    }
  }, []);

  const updateSliderPosition = useCallback((slideIndex) => {
    const container = document.querySelector('.testimonials-container');
    if (container) {
      container.style.transform = `translateX(-${slideIndex * 100}%)`;
    }
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    updateSliderPosition((currentSlide + 1) % totalSlides);
  }, [currentSlide, totalSlides, updateSliderPosition]);

  const handleDialogClose = useCallback((result) => {
    setIsDialogOpen(false);
    if (result) {
      console.log('Form submitted:', result);
      // Handle form submission here
    }
  }, []);

  const handleScroll = useCallback((e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  }, []);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
      updateSliderPosition((currentSlide + 1) % totalSlides);
    }
    if (isRightSwipe) {
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
      updateSliderPosition((currentSlide - 1 + totalSlides) % totalSlides);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    updateCurrentSection();
    // Auto-advance slides every 5 seconds
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [updateCurrentSection, nextSlide]);

  useEffect(() => {
    const handleScroll = () => {
      updateCurrentSection();
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          !event.target.closest('.navbar-toggler')) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [updateCurrentSection, isMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      if (mobile !== isMobile) {
        setIsMobile(mobile);
        setCurrentSlide(0); // Reset to first slide when layout changes
        const container = document.querySelector('.testimonials-container');
        if (container) {
          container.style.transform = 'translateX(0)';
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Handle menu animation
  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuVisible(true);

    } else {
      const timer = setTimeout(() => {
        setIsMenuVisible(false);
      }, 300); // Match this with CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isMenuOpen]);

  // Handle viewport height for mobile devices
  useEffect(() => {
    const setVh = () => {
      if (!heroHeightSet.current) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        heroHeightSet.current = true;
      }
    };

    setVh();
    // Only set height on initial load and orientation change
    window.addEventListener('orientationchange', () => {
      heroHeightSet.current = false;
      setVh();
    });

    return () => {
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  useEffect(() => {
    // Load the background image
    const backgroundImage = new Image();
    // backgroundImage.src = '/assets/banner2_optimized_web.jpg';
    backgroundImage.src = '/assets/background-3.webp';

    backgroundImage.onload = () => {
      setBackgroundImageUrl(backgroundImage.src);
      setIsBackgroundLoaded(true);
      // Only hide loading screen when both content and background are loaded
      if (isContentLoaded) {
        setIsLoading(false);
        setIsFadingOut(true);
        setTimeout(() => {
          setShowLoadingScreen(false);
        }, 500);
      }
    };

    // Set loading state to false after a small delay
    const timer = setTimeout(() => {
      setIsContentLoaded(true);
      // Only hide loading screen when both content and background are loaded
      if (isBackgroundLoaded) {
        setIsLoading(false);
        setIsFadingOut(true);
        setTimeout(() => {
          setShowLoadingScreen(false);
        }, 500);
      }
    }, 100);

    // Preload other images in the background
    const backgroundImages = [
      '/assets/first_photo.webp',
      '/assets/mariinsky-theater.webp',
      '/assets/beginners.webp',
      '/assets/parents.webp',
      '/assets/creative.webp'
    ];

    backgroundImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });

    return () => clearTimeout(timer);
  }, [isContentLoaded, isBackgroundLoaded]);

  const handleLoadingComplete = () => {
    // This is now handled by the timeout in the useEffect
  };

  return (
    <>
      <SEO {...SEO_DATA} />
      {(isLoading || showLoadingScreen) && (
        <LoadingScreen
          onFadeComplete={handleLoadingComplete}
          isFadingOut={isFadingOut}
        />
      )}
      <div className={`main-content ${isContentLoaded ? 'content-loaded' : ''}`}>
        <header>
          <nav className={`navbar navbar-expand-lg navbar-dark fixed-top ${isDialogOpen ? 'dialog-open' : ''}`} role="navigation" aria-label="Основное меню">
            <div className="container">
              <a className="navbar-brand" href="/" aria-label={`${BRAND_NAME} - Главная`}>
                <span className="brand-text">{BRAND_NAME}</span>
              </a>
              <button
                className="navbar-toggler"
                type="button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-controls="navbarNav"
                aria-expanded={isMenuOpen}
                aria-label="Переключить навигацию"
              >
                <span className="custom-toggler navbar-toggler-icon"></span>
              </button>
              <div
                ref={menuRef}
                className={`mobile-menu ${isMenuOpen ? 'menu-open' : ''}`}
                id="navbarNav"
              >
                <ul className="navbar-nav ms-auto">
                  {MENU_ITEMS.map((item) => (
                    <li key={item.id} className="nav-item">
                      <a
                        className={`nav-link ${item.className || ''} ${currentSection === item.id ? 'active' : ''}`}
                        href={`#${item.id}`}
                        onClick={(e) => handleScroll(e, item.id)}
                        aria-label={item.ariaLabel}
                      >
                        <i className={item.icon}></i>
                        <span>{item.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </nav>
        </header>

        <main>
          <section
            className="hero-section"
            role="banner"
            style={{
              backgroundImage: `
                linear-gradient(
                  to bottom,
                  rgba(var(--gradient-color), 0) 0%,
                  rgba(var(--gradient-color), 0) 40%,
                  rgba(var(--gradient-color), 0.3) 50%,
                  rgba(var(--gradient-color), 0.5) 60%,
                  rgba(var(--gradient-color), 0.7) 70%,
                  rgba(var(--gradient-color), 1) 100%
                ),
                url(${backgroundImageUrl})
              `,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="container">
              <div className="hero-content">
                <h1 className="display-4 mb-3" style={{fontSize: '1.8rem'}}>{HERO_SECTION.title}</h1>
                <h1 className="display-4 margin-0" style={{fontSize: '1.8rem'}}>{HERO_SECTION.title_2}</h1>
                <h1 className="display-4 mb-3" style={{ marginBottom: '0rem',fontSize: '1.8rem' }}>{HERO_SECTION.title_3}</h1>
                <div className="hero-buttons mb-4">
                  <button onClick={() => setIsDialogOpen(true)} className="btn btn-primary btn-lg" aria-label="Начать обучение">{HERO_SECTION.primaryButton}</button>
                  <a
                    href="#benefits"
                    className="btn btn-primary btn-lg"
                    onClick={(e) => handleScroll(e, 'benefits')}
                    aria-label="Узнать больше о форматах обучения"
                  >
                    {HERO_SECTION.secondaryButton}
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section id="about-me" className="about-me-section" aria-labelledby="about-me-title">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-md-6 pd-0">
                  <div className="achievement-photo">
                    <img src="/assets/first_photo.webp" alt="Профессиональный преподаватель музыки" className="img-fluid rounded shadow" width="600" height="400" />
                    <div className="subtitle-overlay">
                      <p>{HERO_SECTION.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="container col-md-6">
                  <h2 id="about-me-title" className="section-title text-start">{ABOUT_ME_SECTION.title}</h2>
                  <div className="about-me-content">
                    <p className="lead mb-4">
                      <span className="highlight-text">{ABOUT_ME_SECTION.content.highlight_header},</span>
                      <p>
                      {ABOUT_ME_SECTION.content.header}</p>
                    </p>

                    <p className="lead mb-4">
                      {/* <span className="greeting-text">{ABOUT_ME_SECTION.content.greeting}</span><br /> */}
                      Меня зовут <strong>{ABOUT_ME_SECTION.content.name}</strong><p>{ABOUT_ME_SECTION.content.role}</p>
                    </p>

                    <p className="lead mb-4">
                      <span className="achievement-text">{ABOUT_ME_SECTION.content.achievement_highlight}</span>
                      <p style={{marginBottom: 0}}>{ABOUT_ME_SECTION.content.achievement}</p>
                      <p>{ABOUT_ME_SECTION.content.achievement2}</p>
                    </p>

                    <p className="lead mb-4">
                      <span className="success-text">{ABOUT_ME_SECTION.content.success_highlight}</span>
                      {ABOUT_ME_SECTION.content.success}
                    </p>

                    <p className="lead mb-4">
                      <span className="highlight-text">{ABOUT_ME_SECTION.content.highlight2}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="achievements-section">
            <div className="container">
              <h2 className="section-title secondary-title-color">{ACHIEVEMENTS_SECTION.title}</h2>
              <div className="row justify-content-center">
                <div className="col-lg-8">
                  <ul className="list-unstyled about-achievements">
                    {ACHIEVEMENTS_SECTION.items.map((item, index) => (
                      <li key={index}>
                        <div className="achievement-icon">
                          <i className={item.icon} aria-hidden="true"></i>
                        </div>
                        <div className="achievement-text">
                          <strong>{item.title}</strong>
                          <p>{item.description}</p>
                        </div>
                        {index === 2 && (
                          <div className="achievement-photo" style={{marginBottom: '-2rem'}}>
                            <img
                              src="/assets/mariinsky-theater.webp"
                              alt="Мариинский театр"
                              className="img-fluid rounded shadow"
                            />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="approach-section">
            <div className="container">
              <h2 className="section-title">{APPROACH_SECTION.title}</h2>
              <div className="row g-4">
                {APPROACH_SECTION.cards.map((card, index) => (
                  <div key={index} className="col-md-6">
                    <div className="approach-card">
                      <div className="approach-image">
                        <img src={`/assets/${index === 0 ? 'student-singing' : index === 1 ? 'teaching-combo' : 'makar-playing-2'}.webp`}
                             alt={card.title}
                             className="img-fluid" />
                      </div>
                      <div className="approach-content">
                        <h3>{card.title}</h3>
                        <div className="approach-text">
                          <p className="highlight">{card.highlight}</p>
                          <p>{card.description}</p>
                          <p>{card.description_2}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="benefits" className="section benefits-section" aria-labelledby="benefits-title">
            <div className="container">
              <h2 id="benefits-title" className="section-title secondary-title-color">{BENEFITS_SECTION.title}</h2>
              <div className="row g-4">
                {BENEFITS_SECTION.items.map((item, index) => (
                  <div key={index} className="col-md-4">
                    <article className="card h-100 p-4">
                      <div className="feature-icon" aria-hidden="true">
                        <i className={item.icon}></i>
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="target-audience" className="section target-audience-section">
            <div className="container pd-0">
              <h2 className="section-title pd-lr-15">{TARGET_AUDIENCE_SECTION.title}</h2>
              <div className="row g-4">
                {TARGET_AUDIENCE_SECTION.cards.map((card, index) => (
                  <div key={index} className="col-md-6 pd-0">
                    <div className="card audience-card">
                      <div className="audience-image">
                        <img src={`/assets/${index === 0 ? 'beginners' : index === 1 ? 'parents' : index === 3 ? 'yura-playing' : index === 4 ? 'creative' : 'value_time'}.webp`}
                             alt={card.title}
                             className="img-fluid" />
                      </div>
                      <div className="card-body">
                        <h3 className="card-title">{card.title}</h3>
                        <p className="card-text">{card.description}</p>
                        {card.details.map((detail, i) => (
                          <p key={i} className="card-text">{detail}</p>
                        ))}
                        <ul className="list-unstyled">
                          {card.features.map((feature, i) => (
                            <li key={i}>
                              <i className="fas fa-check text-success me-2"></i>{feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="overcoming-fears" className="section overcoming-fears-section ">
            <div className="container">
              <div className="section-header">
                <h2 className="section-title secondary-title-color">{OVERCOMING_FEARS_SECTION.title}</h2>
              </div>
              <div className="row justify-content-center">
                <div className="col-lg-10">
                  <div className="fears-card">
                    <div className="fears-list">
                      {OVERCOMING_FEARS_SECTION.items.map((item, index) => (
                        <div key={index} className="fears-item">
                          <div className="fears-icon-wrapper">
                            <i className={item.icon}></i>
                          </div>
                          <div className="fears-text">
                            <h4>{item.title}</h4>
                            <p>{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="fears-cta">
                      <p className="lead">{OVERCOMING_FEARS_SECTION.cta.text}</p>
                      <button onClick={() => setIsDialogOpen(true)} className="btn btn-primary">{OVERCOMING_FEARS_SECTION.cta.button}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section id="testimonials" className="section testimonials-section">
            <div className="container">
              <h2 className="section-title">Отзывы учеников</h2>
              <div className="testimonials-slider">
                <div
                  className="testimonials-container"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {testimonialSlides.map((slide, slideIndex) => (
                    <div key={slideIndex} className="testimonial-slide">
                      <div className="row">
                        {slide.map((testimonial) => (
                          <div key={testimonial.id} className="col-md-4">
                            <div className="testimonial-card">
                              <div className="testimonial-content">
                                <p className="testimonial-text">{testimonial.text}</p>
                              </div>
                              <div className="testimonial-author">
                                <div className="testimonial-avatar">
                                  <img src={testimonial.avatar} alt={testimonial.name} />
                                </div>
                                <div className="testimonial-info">
                                  <h4>{testimonial.name}</h4>
                                  <p>{testimonial.role}</p>
                                  <a href={testimonial.link} target="_blank" rel="noopener noreferrer" className="testimonial-link">
                                    Отзыв на {testimonial.link === AVITO_LINK ? "Авито" : testimonial.link === PROFI_LINK ? "Профи" : "Яндекс"}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="testimonials-controls">
                  <button
                    className="testimonial-control prev"
                    onClick={() => {
                      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
                      updateSliderPosition((currentSlide - 1 + totalSlides) % totalSlides);
                    }}
                    aria-label="Предыдущий слайд"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <div className="testimonial-dots">
                    {testimonialSlides.map((_, index) => (
                      <button
                        key={index}
                        className={`testimonial-dot ${currentSlide === index ? 'active' : ''}`}
                        onClick={() => {
                          setCurrentSlide(index);
                          updateSliderPosition(index);
                        }}
                        aria-label={`Перейти к слайду ${index + 1}`}
                      ></button>
                    ))}
                  </div>
                  <button
                    className="testimonial-control next"
                    onClick={() => {
                      setCurrentSlide((prev) => (prev + 1) % totalSlides);
                      updateSliderPosition((currentSlide + 1) % totalSlides);
                    }}
                    aria-label="Следующий слайд"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section id="learning-formats" className="section learning-formats-section">
            <div className="container">
              <h2 className="section-title secondary-title-color">{LEARNING_FORMATS_SECTION.title}</h2>
              <div className="row g-4">
                {LEARNING_FORMATS_SECTION.formats.map((format, index) => (
                  <div key={index} className="col-md-4">
                    <div className={`card format-card ${format.featured ? 'featured' : ''}`}>
                      {format.featured && <div className="featured-badge">Популярно</div>}
                      <div className="format-icon">
                        <i className={format.icon}></i>
                      </div>
                      <div className="card-body">
                        <h3 className="card-title">{format.title}</h3>
                        <p className="card-text">{format.description}</p>
                        <div className="format-features">
                          <h4>{format.forWhom.title}</h4>
                          <ul className="list-unstyled">
                            {format.forWhom.items.map((item, i) => (
                              <li key={i}><i className="fas fa-check text-success me-2"></i>{item}</li>
                            ))}
                          </ul>
                          <h4>{format.features.title}</h4>
                          <ul className="list-unstyled">
                            {format.features.items.map((item, i) => (
                              <li key={i}><i className="fas fa-check text-success me-2"></i>{item}</li>
                            ))}
                          </ul>
                          {format.price && (
                            <div className="format-price mb-2 text-center" style={{borderTop: "1px solid rgba(0, 0, 0, 0.05)", paddingTop: "1rem"}}>
                              <div className="price-title" style={{ fontSize: '0.9em', color: '#888' }}>{format.price_title}</div>
                              <div className="price-value" style={{ fontSize: '1.3em' }}>{format.price}</div>
                            </div>
                          )}
                        </div>
                        <button onClick={() => setIsDialogOpen(true)} className={`btn ${format.featured ? 'btn-primary' : 'btn-primary'} mt-3`}>Записаться</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="location" className="section location-section">
            <div className="container">
              <h2 className="section-title">Где меня найти?</h2>
              <div className="row justify-content-center">
                <div className="col-md-6">
                  <div className="map-container">
                    <iframe
                      src="https://yandex.ru/map-widget/v1/?um=constructor%3Ab47b53527ef46243d0c10832714eaa2557b40c24e6c9e94b48229257e692e88d&amp;source=constructor"
                      width="100%"
                      height="415"
                      frameBorder="0"
                      title="Расположение на карте"
                      aria-label="Карта с местоположением"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="contact" className="section" style={{paddingTop:0}}>
            <div>
              <img src="/assets/with-girl.webp" alt="Профессиональный преподаватель музыки" className="img-fluid rounded shadow"  width="600"  />
            </div>
            <br></br>
            <div className="container">
              <h2 className="section-title">{CONTACT_SECTION.title}</h2>
              <div className="row justify-content-center">
                <div className="col-md-8 text-center">
                  <p className="lead mb-4">{CONTACT_SECTION.description}</p>
                  <button onClick={() => setIsDialogOpen(true)} className="btn btn-primary btn-lg">{CONTACT_SECTION.button}</button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="text-light py-4" role="contentinfo">
          <div className="container text-center">
            <p className="mb-0">{FOOTER.copyright}</p>
          </div>
        </footer>

        {isDialogOpen && (
          <ContactDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
          />
        )}
      </div>
    </>
  );
}

export default App;
